import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { ExpenseCategory, fetchExpenseCategories, PNL_GROUP_OPTIONS, pnlGroupLabel } from './expenseCategoryService';

interface ExpenseCategorySelectProps {
  value: number | null;
  onChange: (id: number | null) => void;
  /** Kayıtlı tür artık pasifse listede görünmez; adı buradan gösterilir. */
  fallbackName?: string | null;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  sx?: object;
}

/** Ortak gider türü listesinden opsiyonel seçim (alış faturası, banka para çekme, kart harcaması). */
const ExpenseCategorySelect: React.FC<ExpenseCategorySelectProps> = ({
  value,
  onChange,
  fallbackName,
  label = 'Gider türü',
  helperText,
  disabled,
  size = 'small',
  fullWidth = true,
  sx,
}) => {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchExpenseCategories(false)
      .then(list => { if (!cancelled) setCategories(list); })
      .catch(() => { if (!cancelled) setCategories([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // groupBy başlıkların tekrarlanmaması için grup sırasına göre dizilmiş liste ister.
  const options = useMemo(() => {
    const list: ExpenseCategory[] = value == null || categories.some(c => c.id === value)
      ? [...categories]
      : [...categories, { id: value, name: `${fallbackName || `Tür #${value}`} (pasif)`, sortOrder: 0, isActive: false, pnlGroup: 'DIGER' }];
    const order = (c: ExpenseCategory) => PNL_GROUP_OPTIONS.findIndex(o => o.value === c.pnlGroup);
    return list.sort((a, b) => order(a) - order(b) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'tr'));
  }, [categories, value, fallbackName]);

  const selected = options.find(c => c.id === value) ?? null;

  return (
    <Autocomplete
      options={options}
      value={selected}
      loading={loading}
      disabled={disabled}
      size={size}
      fullWidth={fullWidth}
      sx={sx}
      getOptionLabel={o => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionDisabled={o => !o.isActive}
      groupBy={o => pnlGroupLabel(o.pnlGroup)}
      onChange={(_, option) => onChange(option ? option.id : null)}
      renderInput={params => <TextField {...params} label={label} helperText={helperText} />}
    />
  );
};

export default ExpenseCategorySelect;
